import React from 'react';
import Chip from '@material-ui/core/Chip';
import { useTranslation } from 'react-i18next';
import type { LifecyclePrediction } from './types';
import css from './DigitalTwinView.module.css';

const formatToken = (value: string): string =>
  value.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

export type DigitalTwinPredictionContextProps = {
  prediction: LifecyclePrediction;
};

export const DigitalTwinPredictionContext: React.FC<DigitalTwinPredictionContextProps> = ({ prediction }) => {
  const { t } = useTranslation();

  return (
    <div className={css.contextPanel}>
      <div className={css.contextGrid}>
        <div className={css.contextBlock}>
          <h3 className={css.contextBlockTitle}>{t('digitalTwin.contextModelQuality')}</h3>
          <div className={css.contextChipRow}>
            <Chip size="small" label={formatToken(prediction.predictionTier)} className={css.chipNeutral} />
            <Chip size="small" label={formatToken(prediction.dataCompleteness)} className={css.chipNeutral} />
            <Chip
              size="small"
              label={`${t('digitalTwin.baselineProfile')}: ${formatToken(prediction.baselineProfile)}`}
              className={css.chipNeutral}
            />
          </div>
          <p className={css.contextSupport}>
            {t('digitalTwin.fitSource')}: {formatToken(prediction.fitSource)} · {prediction.dataPointsUsed}{' '}
            {t('digitalTwin.dataPointsUsed')}
          </p>
        </div>

        <div className={css.contextBlock}>
          <h3 className={css.contextBlockTitle}>{t('digitalTwin.modifiersTitle')}</h3>
          {prediction.modifiersApplied.length > 0 ? (
            <div className={css.contextChipRow}>
              {prediction.modifiersApplied.map((modifier) => (
                <Chip
                  key={modifier}
                  size="small"
                  label={formatToken(modifier)}
                  className={css.chipModifier}
                  variant="outlined"
                />
              ))}
            </div>
          ) : (
            <p className={css.contextEmpty}>{t('digitalTwin.contextNoModifiers')}</p>
          )}
        </div>

        <div className={css.contextBlock}>
          <h3 className={css.contextBlockTitle}>{t('digitalTwin.warningsTitle')}</h3>
          {prediction.warnings.length > 0 ? (
            <div className={css.contextChipRow}>
              {prediction.warnings.map((warning) => (
                <Chip
                  key={warning}
                  size="small"
                  label={formatToken(warning)}
                  className={css.chipWarning}
                  variant="outlined"
                />
              ))}
            </div>
          ) : (
            <p className={css.contextEmpty}>{t('digitalTwin.contextNoWarnings')}</p>
          )}
        </div>
      </div>
    </div>
  );
};
